import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Autocomplete,
  Typography,
} from '@mui/material';
import savsService from '../../services/savsService';
import ordersService from '../../services/ordersService';

const PROBLEM_OPTIONS = [
  'Produit défectueux',
  'Produit reçu endommagé',
  'Produit non conforme',
  'Produit manquant',
  'Taille incorrecte',
  'Couleur incorrecte',
  'Problème de qualité',
  'Autre',
];

const ACTION_OPTIONS = [
  { value: 'remboursement', label: 'Remboursement' },
  { value: 'changement', label: 'Changement' },
  { value: 'avoir', label: 'Avoir' },
  { value: 'reparation', label: 'Réparation' },
  { value: 'autre', label: 'Autre' },
];

const CreateSavModal = ({ open, onClose, onSuccess, orderId: initialOrderId = null }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [orderSearchTerm, setOrderSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    orderId: initialOrderId || '',
    reason: '',
    problem: '',
    productId: '',
    action: 'remboursement',
    notes: '',
    status: 'non_traite',
  });

  useEffect(() => {
    if (open && !initialOrderId) {
      setOrders([]);
      setOrderSearchTerm('');
    } else if (open && initialOrderId) {
      fetchOrderProducts(initialOrderId);
    }
  }, [open, initialOrderId]);

  useEffect(() => {
    if (!open || initialOrderId) return;

    const term = orderSearchTerm.trim();
    if (term.length < 3) {
      setOrders([]);
      return;
    }

    const timer = setTimeout(() => {
      fetchOrders(term);
    }, 300);

    return () => clearTimeout(timer);
  }, [orderSearchTerm, open, initialOrderId]);

  const fetchOrders = async (search = '') => {
    setLoadingOrders(true);
    try {
      const response = await ordersService.getAllOrders(1, 100, { search });
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des commandes:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchOrderProducts = async (orderId) => {
    try {
      const response = await ordersService.getOrderById(orderId);
      const order = response.data.order;
      // Les produits seront disponibles dans order.items
    } catch (error) {
      console.error('Erreur lors de la récupération de la commande:', error);
    }
  };

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    setError(null);
  };

  const handleOrderChange = async (orderId) => {
    setFormData({
      ...formData,
      orderId,
      productId: '', // Reset product when order changes
    });
    if (orderId) {
      await fetchOrderProducts(orderId);
    }
  };

  const getSelectedOrder = () => {
    if (formData.orderId) {
      return orders.find((o) => o.id === parseInt(formData.orderId));
    }
    return null;
  };

  const handleSubmit = async () => {
    if (!formData.orderId || !formData.reason || !formData.problem) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        orderId: parseInt(formData.orderId),
        reason: formData.reason,
        problem: formData.problem,
        productId: formData.productId ? parseInt(formData.productId) : null,
        action: formData.action,
        notes: formData.notes || null,
        status: formData.status,
      };

      await savsService.createSav(payload);
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la création du SAV');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      orderId: initialOrderId || '',
      reason: '',
      problem: '',
      productId: '',
      action: 'remboursement',
      notes: '',
      status: 'non_traite',
    });
    setError(null);
    onClose();
  };

  const selectedOrder = getSelectedOrder();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Créer un nouveau SAV</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            {!!initialOrderId ? (
              <TextField
                fullWidth
                label="Commande"
                value={initialOrderId}
                disabled
              />
            ) : (
              <>
                <Autocomplete
                  options={orders}
                  loading={loadingOrders}
                  value={orders.find((o) => o.id === parseInt(formData.orderId, 10)) || null}
                  onChange={(_, value) => handleOrderChange(value ? value.id : '')}
                  getOptionLabel={(option) =>
                    `${option.orderNumber} - ${option.billingFirstName} ${option.billingLastName} - ${new Date(option.createdAt).toLocaleDateString()}`
                  }
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  noOptionsText={
                    orderSearchTerm.trim().length < 3
                      ? 'Tapez au moins 3 caractères'
                      : 'Aucune commande trouvée'
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label="Commande"
                      placeholder="Rechercher une commande (min 3 caractères)"
                      onChange={(e) => setOrderSearchTerm(e.target.value)}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingOrders ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Recherche en mode contains, résultats à partir de 3 caractères.
                </Typography>
              </>
            )}
          </Grid>

          {selectedOrder && (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Produit concerné</InputLabel>
                <Select
                  value={formData.productId}
                  onChange={handleChange('productId')}
                  label="Produit concerné"
                >
                  <MenuItem value="">Aucun produit spécifique</MenuItem>
                  {selectedOrder.items?.map((item) => (
                    <MenuItem key={item.id} value={item.productId}>
                      {item.productName}
                      {item.variantName && ` - ${item.variantName}`} (Qté: {item.quantity})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Raison du SAV"
              value={formData.reason}
              onChange={handleChange('reason')}
              multiline
              rows={2}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Problème</InputLabel>
              <Select
                value={formData.problem}
                onChange={handleChange('problem')}
                label="Problème"
              >
                {PROBLEM_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Action</InputLabel>
              <Select
                value={formData.action}
                onChange={handleChange('action')}
                label="Action"
              >
                {ACTION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Statut</InputLabel>
              <Select
                value={formData.status}
                onChange={handleChange('status')}
                label="Statut"
              >
                <MenuItem value="non_traite">Non traité</MenuItem>
                <MenuItem value="en_cours">En cours</MenuItem>
                <MenuItem value="fini">Fini</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes (ce qui a été fait)"
              value={formData.notes}
              onChange={handleChange('notes')}
              multiline
              rows={4}
              placeholder="Décrivez ce qui a été fait sur ce SAV..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Annuler
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Créer le SAV'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateSavModal;
